import type { ReactNode } from 'react'
import type { Settings } from '@/types'

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { DEFAULT_SETTINGS } from '@/constants/studio'

type SettingsContextValue = {
    settings: Settings
    ready: boolean
    update: <K extends keyof Settings>(section: K, patch: Partial<Settings[K]>) => void
    reset: () => void
}

const STORAGE_KEY = 'khayal-settings'

const SettingsContext = createContext<SettingsContextValue | null>(null)

const merge = (saved: unknown): Settings => {
    const source = saved && typeof saved === 'object' ? saved as Partial<Record<keyof Settings, unknown>> : {}

    const section = <K extends keyof Settings>(key: K): Settings[K] => {
        const stored = source[key]
        return { ...DEFAULT_SETTINGS[key], ...(stored && typeof stored === 'object' ? stored as Partial<Settings[K]> : {}) }
    }

    return {
        appearance: section('appearance'),
        chat: section('chat'),
        image: section('image'),
        video: section('video'),
        slides: section('slides')
    }
}

const persist = (settings: Settings): void => {
    try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(settings))
    }
    catch {
        // Storage can be unavailable (private mode). Settings still apply for this visit.
    }
}

interface SettingsProviderProps {
    children: ReactNode
}

export const SettingsProvider = ({ children }: SettingsProviderProps): ReactNode => {
    const [settings, setSettings] = useState<Settings>(DEFAULT_SETTINGS)
    const [ready, setReady] = useState(false)

    useEffect(() => {
        try {
            const saved = window.localStorage.getItem(STORAGE_KEY)
            if (saved) setSettings(merge(JSON.parse(saved)))
        }
        catch {
            setSettings(DEFAULT_SETTINGS)
        }

        setReady(true)
    }, [])

    useEffect(() => {
        if (!ready) return

        const root = document.documentElement
        const { theme, accent, fontSize, reduceMotion } = settings.appearance
        const media = window.matchMedia('(prefers-color-scheme: dark)')
        const apply = (): void => {
            root.classList.toggle('dark', theme === 'dark' || (theme === 'system' && media.matches))
        }

        apply()
        root.setAttribute('data-accent', accent)
        root.setAttribute('data-font-size', fontSize)

        if (reduceMotion) root.setAttribute('data-reduce-motion', 'true')
        else root.removeAttribute('data-reduce-motion')

        if (theme !== 'system') return

        media.addEventListener('change', apply)

        return () => media.removeEventListener('change', apply)
    }, [ready, settings.appearance])

    const update = useCallback(<K extends keyof Settings>(section: K, patch: Partial<Settings[K]>): void => {
        setSettings(previous => {
            const next = { ...previous, [section]: { ...previous[section], ...patch } }
            persist(next)
            return next
        })
    }, [])

    const reset = useCallback((): void => {
        persist(DEFAULT_SETTINGS)
        setSettings(DEFAULT_SETTINGS)
    }, [])

    const value = useMemo(() => ({ settings, ready, update, reset }), [settings, ready, update, reset])

    return (
        <SettingsContext.Provider value={value}>
            {children}
        </SettingsContext.Provider>
    )
}

export const useSettings = (): SettingsContextValue => {
    const context = useContext(SettingsContext)

    if (!context) throw new Error('useSettings must be used inside SettingsProvider.')

    return context
}
